/**
 * EditSiteNotesModal Component
 * Modal for editing all site notes (handover, internal, client feedback)
 */

import React, { useState, useEffect } from 'react';
import Modal from '../common/Modal';

const EditSiteNotesModal = ({
  isOpen,
  onClose,
  onConfirm,
  site,
  loading = false
}) => {
  const [handoverNotes, setHandoverNotes] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [clientFeedback, setClientFeedback] = useState('');
  const [activeTab, setActiveTab] = useState('handover');

  useEffect(() => {
    if (isOpen && site) {
      setHandoverNotes(site.handoverNotes || '');
      setInternalNotes(site.internalNotes || '');
      setClientFeedback(site.clientFeedback || '');
    }
  }, [isOpen, site]);

  const handleSubmit = (e) => {
    e.preventDefault();

    // Build changes object - only include fields that changed
    const changes = {};

    if (handoverNotes !== (site?.handoverNotes || '')) {
      changes.handoverNotes = handoverNotes;
    }
    if (internalNotes !== (site?.internalNotes || '')) {
      changes.internalNotes = internalNotes;
    }
    if (clientFeedback !== (site?.clientFeedback || '')) {
      changes.clientFeedback = clientFeedback;
    }

    // Only call confirm if there are changes
    if (Object.keys(changes).length > 0) {
      onConfirm && onConfirm(changes);
    } else {
      onClose();
    }
  };

  const tabs = [
    { key: 'handover', label: 'Handover', color: 'purple' },
    { key: 'internal', label: 'Internal', color: 'blue' },
    { key: 'clientFeedback', label: 'Client Feedback', color: 'green' }
  ];

  const hasChanges =
    handoverNotes !== (site?.handoverNotes || '') ||
    internalNotes !== (site?.internalNotes || '') ||
    clientFeedback !== (site?.clientFeedback || '');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Site Notes" size="lg">
      <form onSubmit={handleSubmit}>
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-4">
          {tabs.map(tab => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? `border-${tab.color}-500 text-${tab.color}-600`
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {/* Show indicator if note exists */}
              {((tab.key === 'handover' && handoverNotes) ||
                (tab.key === 'internal' && internalNotes) ||
                (tab.key === 'clientFeedback' && clientFeedback)) && (
                <span className={`ml-1.5 w-2 h-2 rounded-full bg-${tab.color}-500 inline-block`}></span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="space-y-4">
          {activeTab === 'handover' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Handover Notes
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Notes for project handover to the site team
              </p>
              <textarea
                value={handoverNotes}
                onChange={(e) => setHandoverNotes(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Enter handover notes..."
              />
            </div>
          )}

          {activeTab === 'internal' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Internal Notes
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Internal team notes (not visible to clients)
              </p>
              <textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter internal notes..."
              />
            </div>
          )}

          {activeTab === 'clientFeedback' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Feedback
              </label>
              <p className="text-xs text-gray-500 mb-2">
                Feedback or comments from the client
              </p>
              <textarea
                value={clientFeedback}
                onChange={(e) => setClientFeedback(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                placeholder="Enter client feedback..."
              />
            </div>
          )}
        </div>

        {/* Summary of changes */}
        {hasChanges && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-800 font-medium">Unsaved Changes</p>
            <ul className="text-xs text-amber-700 mt-1 space-y-1">
              {handoverNotes !== (site?.handoverNotes || '') && (
                <li>Handover notes modified</li>
              )}
              {internalNotes !== (site?.internalNotes || '') && (
                <li>Internal notes modified</li>
              )}
              {clientFeedback !== (site?.clientFeedback || '') && (
                <li>Client feedback modified</li>
              )}
            </ul>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !hasChanges}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditSiteNotesModal;
